if (!customElements.get('gg-customer-reviews')) {
  customElements.define('gg-customer-reviews', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.page = 1;
      this.filter = null;
      this.items = [...this.querySelectorAll('[data-review-rating]')];
      this.sort = this.querySelector('[data-review-sort]');
      this.querySelectorAll('[data-rating-filter]').forEach(button => button.addEventListener('click', () => {
        const rating = Number(button.dataset.ratingFilter);
        this.filter = this.filter === rating ? null : rating;
        this.page = 1;
        this.update();
      }, options));
      this.querySelector('[data-clear-filter]').addEventListener('click', () => {
        this.filter = null;
        this.sort.value = 'highest';
        this.page = 1;
        this.update();
      }, options);
      this.querySelectorAll('[data-sort-option]').forEach(button => button.addEventListener('click', () => {
        this.sort.value = button.dataset.sortOption; this.page = 1; this.update();
      }, options));
      document.addEventListener('click', event => {
        const filter = this.querySelector('[data-review-filter]');
        if (filter && !filter.contains(event.target)) filter.open = false;
      }, options);
      this.addEventListener('keydown', event => {
        const filter = this.querySelector('[data-review-filter]');
        if (event.key === 'Escape' && filter?.open) { filter.open = false; filter.querySelector('summary').focus(); }
      }, options);
      this.sort.addEventListener('change' , () => { this.page = 1; this.update(); }, options);
      this.querySelectorAll('[data-open-form]').forEach(button => button.addEventListener('click', () => {
        const panel = this.querySelector(`[data-form-panel="${button.dataset.openForm}"]`);
        const open = panel.hidden;
        this.querySelectorAll('[data-form-panel]').forEach(item => { item.hidden = item !== panel || !open; });
        this.querySelectorAll('[data-open-form]').forEach(item => item.setAttribute('aria-expanded', String(open && item === button)));
        if (open) panel.querySelector('input:not([type=hidden])')?.focus({ preventScroll: true });
      }, options));
      this.querySelectorAll('[data-form-panel]').forEach(panel => {
        if (panel.querySelector('.errors,[role=status]')) {
          panel.hidden = false;
          this.querySelector(`[data-open-form="${panel.dataset.formPanel}"]`)?.setAttribute('aria-expanded','true');
        }
      });
      this.querySelector('[data-review-prev]')?.addEventListener('click', () => { this.page--; this.update(); }, options);
      this.querySelector('[data-review-next]')?.addEventListener('click', () => { this.page++; this.update(); }, options);
      this.update();
      if (this.dataset.reviewsUrl) {
        this.observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) { this.observer.disconnect(); this.loadReviews(); }
        }, { rootMargin: '500px' });
        this.observer.observe(this);
      }
    }
    async loadReviews() {
      try {
        const response = await fetch(this.dataset.reviewsUrl, { signal: this.controller.signal });
        if (!response.ok) throw new Error('Reviews unavailable');
        const reviews = await response.json();
        if (!Array.isArray(reviews) || !reviews.length) return;
        this.items = reviews.filter(review => [4, 5].includes(review.rating) && typeof review.body === 'string' && review.body.trim()).map((review, index) => {
          const item = document.createElement('article');
          item.className = 'gg-cr-item';
          Object.assign(item.dataset, { reviewRating: review.rating, reviewIndex: index, reviewDate: review.date, reviewHelpful: 0, reviewPicture: 'false', reviewVideo: 'false' });
          const stars = document.createElement('span');
          stars.className = 'gg-cr-stars'; stars.setAttribute('role', 'img'); stars.setAttribute('aria-label', `${review.rating} out of 5 stars`);
          stars.textContent = '★'.repeat(review.rating) + '☆'.repeat(5 - review.rating);
          const author = document.createElement('p'); author.className = 'gg-cr-author'; author.textContent = review.author;
          const origin = document.createElement('p'); origin.className = 'gg-cr-origin'; origin.textContent = `Store review · ${review.date}`;
          const body = document.createElement('div'); body.className = 'gg-cr-copy'; body.style.whiteSpace = 'pre-line'; body.textContent = review.body;
          item.append(stars, author, origin, body); return item;
        });
        this.loaded = true; this.update();
      } catch (error) {
        if (error.name !== 'AbortError') this.querySelector('.gg-cr-status').textContent = 'Showing 10 reviews. More reviews could not be loaded.';
      }
    }
    disconnectedCallback() { this.observer?.disconnect(); this.controller?.abort(); this.controller = null; }
    reviewDate(item) {
      const value = item.dataset.reviewDate || '';
      if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return -Infinity;
      const time = Date.parse(`${value}T00:00:00Z`);
      return Number.isFinite(time) && new Date(time).toISOString().slice(0, 10) === value ? time : -Infinity;
    }
    helpfulVotes(item) {
      const count = Number(item.dataset.reviewHelpful);
      return Number.isFinite(count) && count > 0 ? Math.floor(count) : 0;
    }
    update() {
      const list = this.querySelector('.gg-cr-list');
      const sort = this.sort.value;
      const onlyPictures = sort === 'pictures-only';
      const items = [...this.items].sort((a, b) => {
        let difference = 0;
        if (sort === 'highest') difference = Number(b.dataset.reviewRating) - Number(a.dataset.reviewRating);
        if (sort === 'lowest') difference = Number(a.dataset.reviewRating) - Number(b.dataset.reviewRating);
        if (sort === 'recent') difference = this.reviewDate(b) - this.reviewDate(a);
        if (sort === 'oldest') difference = this.reviewDate(a) - this.reviewDate(b);
        if (sort === 'pictures-first') difference = Number(b.dataset.reviewPicture === 'true') - Number(a.dataset.reviewPicture === 'true');
        if (sort === 'videos-first') difference = Number(b.dataset.reviewVideo === 'true') - Number(a.dataset.reviewVideo === 'true');
        if (sort === 'helpful') difference = this.helpfulVotes(b) - this.helpfulVotes(a);
        return difference || Number(a.dataset.reviewIndex) - Number(b.dataset.reviewIndex);
      });
      let count = 0;
      const visible = [];
      for (const item of items) {
        const matchesRating = this.filter === null || Number(item.dataset.reviewRating) === this.filter;
        const matchesMedia = !onlyPictures || item.dataset.reviewPicture === 'true';
        item.hidden = !matchesRating || !matchesMedia;
        if (!item.hidden) count++;
        if (!item.hidden) visible.push(item);
        if (!this.loaded) list.append(item);
      }
      const empty = this.querySelector('.gg-cr-empty');
      if (this.loaded) {
        const pages = Math.max(1, Math.ceil(count / 10)); this.page = Math.max(1, Math.min(this.page, pages));
        list.replaceChildren(...visible.slice((this.page - 1) * 10, this.page * 10), empty);
        this.querySelector('.gg-cr-pagination').hidden = pages <= 1;
        this.querySelector('[data-review-page]').textContent = `${this.page} / ${pages}`;
        this.querySelector('[data-review-prev]').disabled = this.page === 1;
        this.querySelector('[data-review-next]').disabled = this.page === pages;
      }
      empty.hidden = count > 0;
      empty.textContent = onlyPictures ? 'No photo reviews match these filters.' : 'No reviews match these filters.';
      const selectedSort = this.querySelector(`[data-sort-option="${sort}"]`);
      if (selectedSort) this.querySelector('[data-sort-label]').textContent = selectedSort.childNodes[0].textContent.trim();
      this.querySelectorAll('[data-sort-option]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.sortOption === sort)));
      this.querySelectorAll('[data-rating-filter]').forEach(button => button.setAttribute('aria-pressed', String(Number(button.dataset.ratingFilter) === this.filter)));
      let note = '';
      if (sort === 'recent' && this.items.length && this.items.every(item => this.reviewDate(item) === -Infinity)) note = ' · Review dates are unavailable';
      if (sort === 'helpful' && this.items.length && this.items.every(item => this.helpfulVotes(item) === 0)) note = ' · No helpful votes recorded';
      this.querySelector('.gg-cr-status').textContent = `${count} review${count === 1 ? '' : 's'}${note}`;
    }
  });
}
